import React, { useState, useMemo } from 'react';
import {
  FileText, Euro, Search, Eye, X, Download,
  Printer, Mail, AlertCircle, Check, CreditCard
} from 'lucide-react';
import {
  factures, inscriptions, paiements,
  getInscription, getPaiementsByFacture, formatMontant, formatDate
} from '../data/mockData';

const Facturation = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [selectedFacture, setSelectedFacture] = useState(null);

  const totalFacture = factures.reduce((sum, f) => sum + f.totalNet, 0);
  const totalEncaisse = paiements.reduce((sum, p) => sum + p.montant, 0);
  const resteAPercevoir = totalFacture - totalEncaisse;

  const filteredFactures = useMemo(() => {
    return factures.filter(f => {
      const ins = getInscription(f.inscriptionId);
      const eleveName = ins ? `${ins.eleve.prenom} ${ins.eleve.nom}` : '';
      const matchSearch = !searchTerm ||
        eleveName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.numero.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatut = filterStatut === 'tous' || f.statut === filterStatut;
      return matchSearch && matchStatut;
    });
  }, [searchTerm, filterStatut]);

  const getStatusBadge = (statut) => {
    const map = {
      payee: <span className="badge badge-success">Payée</span>,
      partielle: <span className="badge badge-warning">Partielle</span>,
      impayee: <span className="badge badge-danger">Impayée</span>
    };
    return map[statut] || <span className="badge">{statut}</span>;
  };

  const getFacturePaye = (factureId) => {
    return getPaiementsByFacture(factureId).reduce((sum, p) => sum + p.montant, 0);
  };

  const closeModal = () => setSelectedFacture(null);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><FileText size={28} /> Facturation</h1>
          <p style={{ color: 'var(--gray-500)', margin: 0 }}>Gestion des factures générées depuis les inscriptions</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary">
            <Download size={16} /> Exporter CSV
          </button>
        </div>
      </div>

      <div className="finance-cards">
        <div className="finance-card">
          <div className="finance-card-icon" style={{ background: '#DBEAFE', color: '#2563EB' }}>
            <Euro size={24} />
          </div>
          <div className="finance-card-content">
            <span className="finance-label">Total facturé</span>
            <span className="finance-value">{formatMontant(totalFacture)}</span>
          </div>
        </div>
        <div className="finance-card">
          <div className="finance-card-icon" style={{ background: '#D1FAE5', color: '#059669' }}>
            <Check size={24} />
          </div>
          <div className="finance-card-content">
            <span className="finance-label">Total encaissé</span>
            <span className="finance-value">{formatMontant(totalEncaisse)}</span>
          </div>
        </div>
        <div className="finance-card">
          <div className="finance-card-icon" style={{ background: '#FEF3C7', color: '#D97706' }}>
            <AlertCircle size={24} />
          </div>
          <div className="finance-card-content">
            <span className="finance-label">Reste à percevoir</span>
            <span className="finance-value">{formatMontant(resteAPercevoir)}</span>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par élève ou n° de facture…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
        >
          <option value="tous">Tous les statuts</option>
          <option value="payee">Payée</option>
          <option value="partielle">Partielle</option>
          <option value="impayee">Impayée</option>
        </select>
      </div>

      <div className="factures-table-container">
        <table className="factures-table">
          <thead>
            <tr>
              <th>N° Facture</th>
              <th>Élève</th>
              <th>Date</th>
              <th>Lignes</th>
              <th>Total</th>
              <th>Statut</th>
              <th>Payé</th>
              <th>Reste</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFactures.map(facture => {
              const ins = getInscription(facture.inscriptionId);
              const paye = getFacturePaye(facture.id);
              const reste = facture.totalNet - paye;
              return (
                <tr key={facture.id} onClick={() => setSelectedFacture(facture)}>
                  <td>
                    <span className="facture-numero-cell">{facture.numero}</span>
                  </td>
                  <td>
                    {ins ? (
                      <div className="famille-cell">
                        <div className="famille-avatar-mini" style={{ background: ins.color || '#8B5CF6' }}>
                          {ins.initials}
                        </div>
                        <span>{ins.eleve.prenom} {ins.eleve.nom}</span>
                      </div>
                    ) : <span>—</span>}
                  </td>
                  <td>{formatDate(facture.date)}</td>
                  <td>{facture.lignes.length}</td>
                  <td><strong>{formatMontant(facture.totalNet)}</strong></td>
                  <td>{getStatusBadge(facture.statut)}</td>
                  <td>{formatMontant(paye)}</td>
                  <td className={reste > 0 ? 'text-danger' : 'text-success'}>
                    {formatMontant(reste)}
                  </td>
                  <td>
                    <button
                      className="btn-icon"
                      onClick={(e) => { e.stopPropagation(); setSelectedFacture(facture); }}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedFacture && (() => {
        const ins = getInscription(selectedFacture.inscriptionId);
        const facturePayments = getPaiementsByFacture(selectedFacture.id);
        const totalPaye = facturePayments.reduce((sum, p) => sum + p.montant, 0);
        const reste = selectedFacture.totalNet - totalPaye;

        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Détail de la facture</h2>
                <button className="modal-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="facture-detail-header">
                  <div className="facture-info-block">
                    <span className="label">Facture N°</span>
                    <strong>{selectedFacture.numero}</strong>
                  </div>
                  <div className="facture-info-block">
                    <span className="label">Élève</span>
                    <strong>{ins ? `${ins.eleve.prenom} ${ins.eleve.nom}` : '—'}</strong>
                  </div>
                  <div className="facture-info-block">
                    <span className="label">Date</span>
                    <strong>{formatDate(selectedFacture.date)}</strong>
                  </div>
                  <div className="facture-info-block">
                    <span className="label">Statut</span>
                    {getStatusBadge(selectedFacture.statut)}
                  </div>
                </div>

                <div className="facture-lignes">
                  <h4>Lignes de facturation</h4>
                  <table className="lignes-table">
                    <thead>
                      <tr>
                        <th>Produit</th>
                        <th style={{ textAlign: 'right' }}>Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFacture.lignes.map((ligne, idx) => (
                        <tr key={idx}>
                          <td>{ligne.label}</td>
                          <td style={{ textAlign: 'right' }}>{formatMontant(ligne.montant)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td><strong>Total</strong></td>
                        <td style={{ textAlign: 'right' }}><strong>{formatMontant(selectedFacture.totalNet)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="facture-versements">
                  <h4>Versements</h4>
                  {facturePayments.length > 0 ? (
                    <div className="versements-list">
                      {facturePayments.map((paiement, idx) => (
                        <div key={idx} className="versement-item">
                          <span><CreditCard size={14} /> {formatDate(paiement.date)}</span>
                          <span>{paiement.mode}</span>
                          <span style={{ fontWeight: 600 }}>{formatMontant(paiement.montant)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--gray-500)' }}>Aucun versement enregistré</p>
                  )}
                  {reste > 0 && (
                    <div className="versements-reste">
                      <AlertCircle size={16} />
                      <span> Reste à payer : <strong>{formatMontant(reste)}</strong></span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary">
                  <Printer size={16} /> Imprimer
                </button>
                <button className="btn btn-primary">
                  <Mail size={16} /> Envoyer par email
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Facturation;
